import { Box, Typography, Card, CardContent, Grid, Paper } from '@mui/material';
import { Bracket, User, Match } from '../../services/api';

interface PoolStandingsProps {
  bracket: Bracket;
  users: User[];
  matches?: Match[];
}

const PoolStandings = ({ bracket, users, matches }: PoolStandingsProps) => {
  const getUserName = (userId?: string) => {
    if (!userId) return 'TBD';
    const user = users.find(u => u.id === userId);
    return user?.name || 'Unknown';
  };

  // Calculate pool standings from matches
  const calculatePoolStandings = (poolId: string) => {
    const poolMatches = matches?.filter(m => m.pool_id === poolId && m.status === 'completed') || [];
    const standings: { [playerId: string]: { wins: number; losses: number; setsWon: number; setsLost: number } } = {};
    
    // Initialize standings for all players in pool
    const pool = bracket.pools?.find(p => p.id === poolId);
    pool?.player_ids.forEach(playerId => {
      standings[playerId] = { wins: 0, losses: 0, setsWon: 0, setsLost: 0 };
    });
    
    // Calculate from completed matches
    poolMatches.forEach((match: Match) => {
      if (match.winner_id && match.player1_id && match.player2_id) {
        const winnerId = match.winner_id;
        const loserId = match.player1_id === winnerId ? match.player2_id : match.player1_id;
        
        if (standings[winnerId]) standings[winnerId].wins++;
        if (loserId && standings[loserId]) standings[loserId].losses++;
        
        // Count sets if score available
        if (match.score?.sets) {
          match.score.sets.forEach((set: any) => {
            if (set.player1_games > set.player2_games) {
              if (match.player1_id === winnerId && match.player1_id && match.player2_id) {
                if (standings[match.player1_id]) standings[match.player1_id].setsWon++;
                if (standings[match.player2_id]) standings[match.player2_id].setsLost++;
              }
            } else {
              if (match.player2_id === winnerId && match.player1_id && match.player2_id) {
                if (standings[match.player2_id]) standings[match.player2_id].setsWon++;
                if (standings[match.player1_id]) standings[match.player1_id].setsLost++;
              }
            }
          });
        }
      }
    });
    
    return standings;
  };

  if (!bracket.pools || bracket.pools.length === 0) {
    return (
      <Typography color="text.secondary">
        No pool standings available
      </Typography>
    );
  }

  return (
    <Box>
      <Grid container spacing={2}>
        {bracket.pools.map((pool) => {
          const standings = calculatePoolStandings(pool.id);
          const sortedPlayers = [...pool.player_ids].sort((a, b) => {
            const aStats = standings[a];
            const bStats = standings[b];
            if (aStats.wins !== bStats.wins) return bStats.wins - aStats.wins;
            if (aStats.setsWon !== bStats.setsWon) return bStats.setsWon - aStats.setsWon;
            return 0;
          });
          
          return (
            <Grid item xs={12} md={6} lg={4} key={pool.id}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">
                    {pool.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                    Winner advances to elimination bracket
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {sortedPlayers.map((playerId, index) => {
                      const stats = standings[playerId];
                      return (
                        <Paper key={playerId} sx={{ p: 1.5, bgcolor: 'background.default' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2">
                              {index + 1}. {getUserName(playerId)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {stats.wins}-{stats.losses}
                            </Typography>
                          </Box>
                        </Paper>
                      );
                    })}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default PoolStandings;
